/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
/* eslint-disable ember/no-runloop */
import type Store from "@ember-data/store";
import { action } from "@ember/object";
import type RouterService from "@ember/routing/router-service";
import { next } from "@ember/runloop";
import { service } from "@ember/service";
import Component from "@glimmer/component";
import DraftManager from "parog-web/components/forc/draft-manager";
import FormAction from "parog-web/components/forc/form-action";
import type Patient from "parog-web/models/patient";
import { Changeset } from "ember-changeset";
import { task, timeout } from "ember-concurrency";
import type AnalyticsService from "parog-web/services/analytics";
import type SemeiaSessionService from "parog-web/services/session";

import styles from "./index.module.css";

interface Args {
  patient: Patient;
  autoFocusEnabled: boolean;
}

const messageContentKey = "messageContent";

export default class ChatContentComponent extends Component<Args> {
  styles = styles;

  @service declare session: SemeiaSessionService;
  @service declare router: RouterService;
  @service declare store: Store;
  @service declare analytics: AnalyticsService;

  get patient(): Patient {
    return this.args.patient;
  }

  /*** MESSAGE CONTENT DRAFT MANAGER ***/
  changeset = Changeset({});
  fullComponentId = "chat/content";
  autoSaveDraft = true;
  formAction = FormAction.New;
  messageContentDraft = new DraftManager(this);

  constructor(owner: unknown, args: Args) {
    super(owner, args);

    this.changeset.on("afterValidation", () => {
      this.messageContentDraft.onDidUpdate();
    });

    const hasDraft = DraftManager.hasDraftFor(
      DraftManager.localStorageKey(this.fullComponentId, this.patient)
    );

    if (hasDraft) {
      next(this, function () {
        this.messageContentDraft.load();
      });
    } else {
      next(this, function () {
        this.changeset.set(messageContentKey, "");
      });
    }

    if (this.args.autoFocusEnabled && !this.restrictedMessaging) {
      this._focus();
    }

    // Process all chat messages notifs for this patient
    // if we are rendering this component when logged as a medic
    // HACK: This needs to be run in the next runloop to avoid simultaneous read+write on tracked objects
    // this is probably a code smell!
    if (this.session.medic)
      next(args.patient, args.patient.processChatNotifications);

    setTimeout(this._scrollToBottom, 0);
  }

  get restrictedMessaging(): boolean {
    return (
      Boolean(this.session.patient) &&
      this.args.patient.healthCentre.hasRestrictedMessaging
    );
  }

  get shouldShowInput(): boolean {
    // Always show the input for a Medic
    if (this.session.medic) {
      return true;
    }

    // When the user is a patient
    // Show the input when the restrictedMessaging is off
    // Or the message content is set by selecting a suggestion
    return (
      !this.restrictedMessaging ||
      (this.restrictedMessaging &&
        Boolean(this.changeset.get(messageContentKey)))
    );
  }

  @action
  onSuggestionSelected(selectedSuggestion: string): void {
    let message;
    switch (selectedSuggestion) {
      case "medical-prescription":
        message =
          "Bonjour, mon ordonnance sur la plateforme n’est plus à jour et/ou j’ai besoin d’une nouvelle ordonnance de médicaments.";
        break;
      case "exam-prescription":
        message =
          "Bonjour, mon ordonnance sur la plateforme n’est plus à jour et/ou j’ai besoin d’une nouvelle ordonnance d’examens médicaux.";
        break;
      case "bio-prescription":
        message =
          "Bonjour, mon ordonnance sur la plateforme n’est plus à jour et/ou j’ai besoin d’une nouvelle ordonnance d’examen biologiques.";
        break;
      default:
        break;
    }

    this.changeset.set(messageContentKey, message);
    this.scrollAndFocus();
  }

  /**
   * Can be called either from medics web or patient web
   *
   * So the transition & callback URL differs according
   * to the current user type
   */
  @action
  onFileUploadClicked(): void {
    const editionCallbackUrl = this.session.medic
      ? `/medics-web/patients/${this.args.patient.id}/chat`
      : "/patient-web/chat";

    if (this.session.medic) {
      this.router.transitionTo(this.router.currentRouteName, {
        queryParams: {
          tab: "upload-document"
        }
      });
    } else {
      this.router.transitionTo(
        `patient-web.protected.feature.component`,
        "document",
        "edition-form",
        {
          queryParams: {
            callbackUrl: editionCallbackUrl
          }
        }
      );
    }
  }

  /**
   * Can be called only on patient web
   */
  @action
  onMedicalEventClicked(): void {
    this.router.transitionTo(
      `patient-web.protected.feature.component`,
      "medical-event",
      "edition-form",
      {
        queryParams: {
          callbackUrl: "/patient-web/chat"
        }
      }
    );
  }

  @action
  onSendMessage(): void {
    this.sendMessageTask.perform();
    this._focus();
  }

  sendMessageTask = task({ enqueue: true }, async () => {
    if (!this.isMessageValid) return;

    const messageEvent = this.store.createRecord(
      "patient-chat-message-sent-journal-event",
      {
        patient: this.args.patient,
        creator: this.session.medic || this.session.patient,
        occurredAt: new Date(),
        body: this.cleanedMessage
      }
    );

    await messageEvent.save();
    this.analytics.trackJeCreation(messageEvent);

    this.changeset.set(messageContentKey, "");
    this.messageContentDraft.onDidSave();
    // setTimeout is needed otherwise it scrolls before the dom is updated
    setTimeout(this._scrollToBottom, 0);

    // Helps the user understanding that something is happening.
    await timeout(200);
  });

  get cleanedMessage(): string {
    return this.changeset.get(messageContentKey).trim();
  }

  get isMessageValid(): boolean {
    return Boolean(this.cleanedMessage) && this.cleanedMessage !== "";
  }

  @action
  scrollAndFocus(): void {
    setTimeout(this._scrollToBottom, 0);
    if (!this.restrictedMessaging) {
      this._focus();
    }
  }

  _focus(): void {
    (document.querySelector(".ui.input") as HTMLInputElement).focus();
  }

  _scrollToBottom(): void {
    const element = document.querySelector('[data-test-sidebar-content=""]');
    if (element) {
      const offset = element.scrollHeight - element.clientHeight;
      element.scrollTo(0, offset);
    } else {
      window.scrollTo(0, document.body.scrollHeight);
    }
  }
}
